import { computePosition, autoUpdate, offset, flip, shift } from "@floating-ui/dom";
import { I80Element, define, h, nextId, lateLabel } from "../base.js";
import { icon } from "../icons.js";

const POSITIONS = { "bottom-right": "bottom-end", "bottom-left": "bottom-start", "top-right": "top-end", "top-left": "top-start" };
const ITEM_TAGS = ["i80-dropdown-item", "i80-dropdown-separator", "i80-dropdown-title"];

/**
 * <i80-dropdown text="Actions" color="secondary">
 *   <i80-dropdown-item href="/plans/1/edit" icon="edit">Edit</i80-dropdown-item>
 *   <i80-dropdown-separator></i80-dropdown-separator>
 *   <i80-dropdown-item color="critical" icon="trash">Delete</i80-dropdown-item>
 * </i80-dropdown>
 *
 * A toggle button and a list in a native popover (Esc, click outside and the top layer
 * come from the browser), positioned with Floating UI. Arrow keys move between items.
 * Items are real links/buttons; an item's own <a>, <button> or <form> is used as is.
 */
export class I80Dropdown extends I80Element {
  static get observedAttributes() {
    return ["text", "color", "size", "icon", "full-width", "position", "width", "height", "inline", "match-toggle-width", "open"];
  }

  setup() {
    let root = this.part("root");
    if (!root) {
      const items = [...this.children].filter((c) => ITEM_TAGS.includes(c.localName));
      const list = h("ul", { class: "i80-dropdown__list" }, items.map((item) => h("li", {}, item)));
      const content = h("div", { class: "i80-dropdown__content", popover: "auto", tabindex: "-1", "data-i80-part": "content" }, list);
      const toggle = h("button", { type: "button", "data-i80-part": "toggle" });
      root = h("div", { class: "i80-dropdown", "data-i80-part": "root" }, toggle, content);
      this.append(root);
    }
    const toggle = root.querySelector('[data-i80-part="toggle"]');
    const content = root.querySelector('[data-i80-part="content"]');
    // New ids on every instance, so clones never share them.
    toggle.id = nextId("i80-dropdown-toggle");
    content.id = nextId("i80-dropdown-content");
    toggle.setAttribute("popovertarget", content.id);
    toggle.setAttribute("aria-controls", content.id);
    this._root = root;
    this._toggle = toggle;
    this._content = content;

    content.addEventListener("toggle", (e) => this._onToggle(e.newState === "open"));
    toggle.addEventListener("keydown", (e) => {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        this.show();
        this._focusItem(e.key === "ArrowDown" ? 0 : -1);
      }
    });
    content.addEventListener("keydown", (e) => this._onListKey(e));
    // Close after choosing an item (links navigate anyway; buttons act and close).
    content.addEventListener("click", (e) => {
      const item = e.target.closest("a, button");
      if (item && content.contains(item) && !item.closest("[keep-open]")) this.hide();
    });
    // Tabbing out of the list closes it, like leaving any other disclosure.
    content.addEventListener("focusout", (e) => {
      if (e.relatedTarget && !content.contains(e.relatedTarget) && e.relatedTarget !== toggle) this.hide();
    });
  }

  adopt() {
    const list = this._content.querySelector(".i80-dropdown__list");
    for (const item of [...this.children].filter((c) => ITEM_TAGS.includes(c.localName))) {
      list.append(h("li", {}, item));
    }
  }

  update() {
    const size = this.choice("size", ["small", "medium"], "medium");
    const name = this.getAttribute("icon");
    const open = this._content.matches(":popover-open");
    this._toggle.className = [
      "i80-dropdown-toggle-button",
      `i80-dropdown-toggle-button--size-${size}`,
      `i80-dropdown-toggle-button--color-${this.choice("color", ["primary", "secondary"], "primary")}`,
      this.flag("full-width") && "i80-dropdown-toggle-button--width-full",
      open && "i80-dropdown-toggle-button--is-open",
    ].filter(Boolean).join(" ");
    this._toggle.setAttribute("aria-expanded", String(open));
    this._toggle.replaceChildren(
      ...(name ? [h("div", { class: "i80-dropdown-toggle-button__icon" }, icon(name, { stretched: true }))] : []),
      h("div", { class: "i80-dropdown-toggle-button__text" }, this.getAttribute("text") ?? ""),
      h("div", { class: "i80-dropdown-toggle-chevron" }, icon("chevron-down")),
    );
    this._root.className = ["i80-dropdown", this.flag("inline") && "i80-dropdown--is-inline"].filter(Boolean).join(" ");
    const position = this.choice("position", Object.keys(POSITIONS), "bottom-right");
    const fixed = this.hasAttribute("width") || this.flag("match-toggle-width");
    this._content.className = ["i80-dropdown__content", `i80-dropdown__content--position-${position}`, fixed && "i80-dropdown__content--fixed-width"].filter(Boolean).join(" ");
    this._content.style.width = this.getAttribute("width") ?? "";
    this._content.style.maxHeight = this.getAttribute("height") ?? "";
    if (this.flag("open") !== open) (this.flag("open") ? this.show() : this.hide());
  }

  show() {
    if (!this._content.matches(":popover-open")) this._content.showPopover();
  }

  hide() {
    if (this._content.matches(":popover-open")) this._content.hidePopover();
  }

  get items() {
    return [...this._content.querySelectorAll("li a[href], li button:not([disabled])")];
  }

  _focusItem(index) {
    const items = this.items;
    if (items.length) items[(index + items.length) % items.length].focus();
  }

  _onListKey(e) {
    const items = this.items;
    const i = items.indexOf(document.activeElement);
    const moves = { ArrowDown: i + 1, ArrowUp: i - 1, Home: 0, End: -1 };
    if (e.key in moves) {
      e.preventDefault();
      this._focusItem(moves[e.key]);
    }
  }

  _onToggle(open) {
    this._toggle.setAttribute("aria-expanded", String(open));
    this._toggle.classList.toggle("i80-dropdown-toggle-button--is-open", open);
    if (open) {
      const placement = POSITIONS[this.choice("position", Object.keys(POSITIONS), "bottom-right")];
      const match = this.flag("match-toggle-width");
      this._stopPositioning = autoUpdate(this._toggle, this._content, () =>
        computePosition(this._toggle, this._content, {
          placement,
          strategy: "fixed",
          middleware: [offset(4), flip({ padding: 8 }), shift({ padding: 8 })],
        }).then(({ x, y }) => {
          Object.assign(this._content.style, { position: "fixed", margin: "0", left: `${x}px`, top: `${y}px` });
          if (match) this._content.style.width = `${this._toggle.offsetWidth}px`;
        }),
      );
    } else {
      this._stopPositioning?.();
    }
    this.dispatchEvent(new CustomEvent(open ? "i80-open" : "i80-close", { bubbles: true }));
  }
}

const ITEM_COLORS = ["action", "critical"];

/** One entry: a link (href), a button, or the author's own <a>/<button>/<form>. */
export class I80DropdownItem extends I80Element {
  static get observedAttributes() {
    return ["href", "icon", "trailing-icon", "color", "disabled", "external"];
  }

  setup() {
    let control = this.part("control") ?? this.querySelector("a, button");
    if (!control) {
      control = this.hasAttribute("href") ? h("a") : h("button", { type: this.getAttribute("type") || "button" });
      control.append(...this.takeContent());
      this.append(control);
    }
    control.setAttribute("data-i80-part", "control");
    const existing = control.querySelector(".i80-dropdown-list-item__interactive-text");
    this._label = (existing ?? control).textContent.trim();
    this._control = control;
  }

  adopt() {
    const label = lateLabel(this);
    if (label) this._label = label;
  }

  update() {
    const c = this._control;
    const li = this.closest("li");
    if (li) li.className = `i80-dropdown-list-item i80-dropdown-list-item--variant-interactive i80-dropdown-list-item--color-${this.choice("color", ITEM_COLORS, "action")}`;
    if (c.localName === "a") {
      if (this.hasAttribute("href")) c.setAttribute("href", this.getAttribute("href"));
      if (this.flag("external")) Object.assign(c, { target: "_blank", rel: "noopener noreferrer" });
    } else {
      c.disabled = this.flag("disabled");
    }
    const lead = this.getAttribute("icon");
    const trail = this.getAttribute("trailing-icon");
    c.replaceChildren(
      ...(lead ? [h("span", { class: "i80-dropdown-list-item__interactive-icon i80-dropdown-list-item__interactive-icon--leading" }, icon(lead))] : []),
      h("span", { class: "i80-text i80-typography-body-200 i80-font-weight-medium i80-dropdown-list-item__interactive-text" }, this._label),
      ...(trail ? [h("span", { class: "i80-dropdown-list-item__interactive-icon i80-dropdown-list-item__interactive-icon--trailing" }, icon(trail))] : []),
    );
  }
}

export class I80DropdownSeparator extends I80Element {
  update() {
    const li = this.closest("li");
    if (li) {
      li.className = "i80-dropdown-list-item i80-dropdown-list-item--variant-separator";
      li.setAttribute("role", "separator");
      li.setAttribute("aria-hidden", "true");
    }
  }
}

export class I80DropdownTitle extends I80Element {
  update() {
    const li = this.closest("li");
    if (li) li.className = "i80-text i80-typography-body-100 i80-font-weight-semibold i80-foreground-strong i80-dropdown-list-item i80-dropdown-list-item--variant-title";
  }
}

define("i80-dropdown", I80Dropdown);
define("i80-dropdown-item", I80DropdownItem);
define("i80-dropdown-separator", I80DropdownSeparator);
define("i80-dropdown-title", I80DropdownTitle);
